/**
 * 벌점 시스템 타입 정의
 */

// 상벌점 유형
export interface PenaltyType {
  id: string;
  organization_id: string;
  name: string;
  code: string;
  default_points: number;
  description?: string;
  point_type: 'merit' | 'demerit';  // merit(상점) / demerit(벌점)
  is_system: boolean;
  is_auto: boolean;
  is_active: boolean;
  display_order: number;
  created_at: string;
}

export interface PenaltyTypeCreate {
  organization_id: string;
  name: string;
  code: string;
  default_points?: number;
  description?: string;
  point_type?: 'merit' | 'demerit';  // 상점/벌점
  display_order?: number;
}

export interface PenaltyTypeUpdate {
  name?: string;
  default_points?: number;
  description?: string;
  point_type?: 'merit' | 'demerit';  // 상점/벌점 전환
  is_active?: boolean;
  display_order?: number;
}

// 벌점 설정
export interface PenaltySetting {
  id: string;
  organization_id: string;
  auto_penalty_enabled: boolean;
  reset_period: 'monthly' | 'semester' | 'manual';
  reset_day: number;
  semester_reset_months?: number[];
  last_reset_date?: string;
  excused_absence_points?: number;
  excused_late_points?: number;
  excused_early_points?: number;
  created_at: string;
  updated_at: string;
}

export interface PenaltySettingUpdate {
  auto_penalty_enabled?: boolean;
  reset_period?: 'monthly' | 'semester' | 'manual';
  reset_day?: number;
  semester_reset_months?: number[];
  excused_absence_points?: number;
  excused_late_points?: number;
  excused_early_points?: number;
}

// 벌점 임계값
export interface PenaltyThreshold {
  id: string;
  organization_id: string;
  name: string;
  points: number;
  color: string;
  description?: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
}

export interface PenaltyThresholdCreate {
  organization_id: string;
  name: string;
  points: number;
  color?: string;
  description?: string;
  display_order?: number;
}

export interface PenaltyThresholdUpdate {
  name?: string;
  points?: number;
  color?: string;
  description?: string;
  is_active?: boolean;
  display_order?: number;
}

// 상벌점 기록
export interface PenaltyRecord {
  id: string;
  student_id: string;
  organization_id: string;
  penalty_type_id?: string;
  penalty_type: string;
  points: number;
  point_type: 'merit' | 'demerit';  // merit(상점) / demerit(벌점)
  reason: string;
  occurred_date: string;
  occurred_time?: string;
  reference_type?: string;
  reference_id?: string;
  issued_by?: string;
  is_auto: boolean;
  is_cancelled: boolean;
  cancelled_at?: string;
  cancelled_by?: string;
  cancel_reason?: string;
  original_points?: number;
  reduced_points?: number;
  reduce_reason?: string;
  reduced_at?: string;
  created_at: string;
  student_name?: string;
  penalty_type_name?: string;
}

export interface PenaltyRecordCreate {
  student_id: string;
  organization_id: string;
  penalty_type_code: string;
  points: number;
  point_type?: 'merit' | 'demerit';
  reason: string;
  occurred_date: string;
  occurred_time?: string;
  issued_by?: string;
}

// 상벌점 현황
export interface PenaltySummary {
  id: string;
  student_id: string;
  organization_id: string;
  current_points: number;
  merit_points: number;       // 상점 합계
  demerit_points: number;     // 벌점 합계
  period_start_date: string;
  total_points_ever: number;
  penalty_count: number;
  current_threshold_id?: string;
  last_threshold_reached_at?: string;
  updated_at: string;
  student_name?: string;
  threshold_name?: string;
  threshold_color?: string;
}

// 벌점 통계
export interface PenaltyStatistics {
  total_students: number;
  students_with_penalty: number;
  total_penalty_points: number;
  average_points: number;
  type_counts: Record<string, number>;
}

// 필터/검색 파라미터
export interface PenaltyRecordFilter {
  student_id?: string;
  start_date?: string;
  end_date?: string;
  penalty_type?: string;
  include_cancelled?: boolean;
}

export interface PenaltySummaryFilter {
  min_points?: number;
}

// 컴포넌트 상태
export interface PenaltyManagementState {
  activeTab: 'summary' | 'records' | 'settings';
  selectedStudent?: string;
  isLoading: boolean;
  error?: string;
}

// 색상 매핑
export const THRESHOLD_COLORS: Record<string, string> = {
  yellow: '#fbbf24',
  orange: '#f97316',
  red: '#ef4444',
  purple: '#a855f7',
  gray: '#6b7280',
};

// 기본 임계값 색상 옵션
export const THRESHOLD_COLOR_OPTIONS = [
  { value: 'yellow', label: '노란색', hex: '#fbbf24' },
  { value: 'orange', label: '주황색', hex: '#f97316' },
  { value: 'red', label: '빨간색', hex: '#ef4444' },
  { value: 'purple', label: '보라색', hex: '#a855f7' },
  { value: 'gray', label: '회색', hex: '#6b7280' },
];

// 초기화 주기 옵션
export const RESET_PERIOD_OPTIONS = [
  { value: 'monthly', label: '매월' },
  { value: 'semester', label: '학기별' },
  { value: 'manual', label: '수동' },
];
